import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { pageSEO } from '../utils/seo';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  localizeCarClass,
  localizeFuel,
  localizeSeatLabel,
  localizeVehicleModel,
} from '../utils/localization';

interface VehicleData {
  id: number;
  model_name: string;
  category: string;
  fuel_type: string;
  seats: number;
  daily_rate: number;
  image_url?: string;
}

interface TableVehicleData {
  id: string;
  name: string;
  category: string;
  fuel: string;
  seats: string;
  price5h_weekday?: string;
  price5h_weekend?: string;
  price12h_weekday?: string;
  price12h_weekend?: string;
  price24h_weekday?: string;
  price24h_weekend?: string;
  price48h_weekday?: string;
  price48h_weekend?: string;
  imageId?: string;
}

const RentalGuidePage: React.FC = () => {
  const { i18n } = useTranslation();
  const [vehicles, setVehicles] = useState<TableVehicleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const seo = pageSEO.rentalGuide;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVehicles();
  }, []);

  const isEnglish = i18n.language.startsWith('en');
  const text = {
    title: isEnglish ? 'Vehicle Guide' : '차량안내',
    subtitle: isEnglish ? 'Choose from a wide range of vehicles for your trip' : '다양한 차종으로 고객님의 여행을 함께합니다',
    loading: isEnglish ? 'Loading vehicle information...' : '차량 정보를 불러오는 중...',
    image: isEnglish ? 'Vehicle Image' : '차량 이미지',
    name: isEnglish ? 'Vehicle Name' : '차량명',
    type: isEnglish ? 'Type' : '차종',
    fuel: isEnglish ? 'Fuel' : '연료',
    seats: isEnglish ? 'Seats' : '탑승인원',
    rateTitle: isEnglish ? 'Rental Rates' : '대여 요금',
    weekday: isEnglish ? 'Weekday' : '주중',
    weekend: isEnglish ? 'Weekend' : '주말',
    hour: isEnglish ? 'hr' : '시간',
    won: isEnglish ? ' KRW' : '원',
    noticeTitle: isEnglish ? 'Notice' : '안내사항',
    notices: isEnglish
      ? [
          'Displayed prices are base rental fees and may vary on weekends, peak seasons, and holidays.',
          'Use real-time booking to check exact prices and vehicle availability.',
          'Insurance and additional option fees are separate.',
          'Vehicle images are for reference and may differ from the actual vehicle.',
          "A '-' means that rental duration is not available for that vehicle.",
        ]
      : [
          '표시된 가격은 기본 대여료이며, 주말/성수기/연휴에는 요금이 상이할 수 있습니다',
          '실시간 예약 시 정확한 가격과 차량 가용성을 확인하실 수 있습니다',
          '보험료 및 추가 옵션 요금은 별도입니다',
          '차량 이미지는 참고용이며 실제 차량과 다를 수 있습니다',
          "'-'로 표시된 시간대는 해당 옵션이 제공되지 않습니다",
        ],
  };

  const displayVehicleName = (name: string) => localizeVehicleModel(name, i18n.language);
  const displayCategory = (category: string) => localizeCarClass(category, i18n.language);
  const displayFuel = (fuel: string) => localizeFuel(fuel, i18n.language);
  const displaySeats = (seats: string) => localizeSeatLabel(seats, i18n.language);
  const displayPrice = (price?: string) => price && price !== '-' ? `${price}${text.won}` : '-';

  const fetchVehicles = async () => {
    try {
      const response = await axios.get<VehicleData[]>(`${process.env.REACT_APP_API_URL}/vehicles`);
      const vehicleData: TableVehicleData[] = response.data.map((v) => ({
        id: v.id.toString(),
        name: v.model_name,
        category: getCategoryKorean(v.category),
        fuel: getFuelTypeKorean(v.fuel_type),
        seats: `${v.seats}인`,
        imageId: v.id.toString(),
        // 가격은 임시로 설정 (실제로는 API에서 시간대별 가격을 받아와야 함)
        price12h_weekday: formatPrice(v.daily_rate / 2),
        price12h_weekend: formatPrice(v.daily_rate / 2),
        price24h_weekday: formatPrice(v.daily_rate),
        price24h_weekend: formatPrice(v.daily_rate),
        price48h_weekday: formatPrice(v.daily_rate * 1.8),
        price48h_weekend: formatPrice(v.daily_rate * 1.8),
      }));
      setVehicles(vehicleData);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      // API 실패 시 기본 데이터 사용
      setVehicles(getDefaultVehicles());
    } finally {
      setLoading(false);
    }
  };

  const getCategoryKorean = (category: string): string => {
    const map: { [key: string]: string } = {
      'compact': '경차',
      'sedan': '준중형',
      'midsize': '중형',
      'fullsize': '준대형',
      'suv': 'SUV',
      'van': '승합',
      'minibus': '미니버스',
      'electric': '전기차',
    };
    return map[category.toLowerCase()] || category;
  };

  const getFuelTypeKorean = (fuelType: string): string => {
    const map: { [key: string]: string } = {
      'gasoline': '가솔린',
      'diesel': '디젤',
      'lpg': 'LPG',
      'hybrid': '하이브리드',
      'electric': '전기',
    };
    return map[fuelType.toLowerCase()] || fuelType;
  };

  const formatPrice = (price: number): string => {
    return Math.round(price / 1000) * 1000 + ',000'.replace(',000', ',000');
  };

  const getDefaultVehicles = (): TableVehicleData[] => [
    // 경차 & 준중형
    { id: '1', name: '레이', category: '경차', fuel: '가솔린', seats: '5인',
      price5h_weekday: '30,000', price5h_weekend: '-',
      price12h_weekday: '45,000', price12h_weekend: '50,000',
      price24h_weekday: '55,000', price24h_weekend: '60,000',
      price48h_weekday: '110,000', price48h_weekend: '120,000',
      imageId: 'ray' },
    { id: '2', name: '캐스퍼', category: '경차', fuel: '가솔린', seats: '5인',
      price5h_weekday: '35,000', price5h_weekend: '-',
      price12h_weekday: '50,000', price12h_weekend: '55,000',
      price24h_weekday: '60,000', price24h_weekend: '65,000',
      price48h_weekday: '120,000', price48h_weekend: '130,000',
      imageId: 'casper' },
    { id: '3', name: '아반떼 AD', category: '준중형', fuel: '가솔린', seats: '5인',
      price5h_weekday: '35,000', price5h_weekend: '-',
      price12h_weekday: '50,000', price12h_weekend: '55,000',
      price24h_weekday: '60,000', price24h_weekend: '65,000',
      price48h_weekday: '120,000', price48h_weekend: '130,000',
      imageId: 'avante-ad' },
    { id: '4', name: '아반떼 CN7', category: '준중형', fuel: '가솔린', seats: '5인',
      price5h_weekday: '45,000', price5h_weekend: '-',
      price12h_weekday: '65,000', price12h_weekend: '70,000',
      price24h_weekday: '75,000', price24h_weekend: '80,000',
      price48h_weekday: '150,000', price48h_weekend: '160,000',
      imageId: 'avante-cn7' },
    { id: '5', name: '아반떼 CN7 FL', category: '준중형', fuel: '가솔린', seats: '5인',
      price5h_weekday: '50,000', price5h_weekend: '-',
      price12h_weekday: '70,000', price12h_weekend: '75,000',
      price24h_weekday: '80,000', price24h_weekend: '85,000',
      price48h_weekday: '160,000', price48h_weekend: '170,000',
      imageId: 'avante-cn7-fl' },
    { id: '6', name: '베뉴', category: '준중형', fuel: '가솔린', seats: '5인',
      price5h_weekday: '35,000', price5h_weekend: '-',
      price12h_weekday: '50,000', price12h_weekend: '55,000',
      price24h_weekday: '60,000', price24h_weekend: '65,000',
      price48h_weekday: '120,000', price48h_weekend: '130,000',
      imageId: 'venue' },

    // 중형 & 대형
    { id: '7', name: '3세대 K5', category: '중형', fuel: 'LPG', seats: '5인',
      price5h_weekday: '50,000', price5h_weekend: '-',
      price12h_weekday: '80,000', price12h_weekend: '80,000',
      price24h_weekday: '90,000', price24h_weekend: '90,000',
      price48h_weekday: '180,000', price48h_weekend: '180,000',
      imageId: 'k5' },
    { id: '8', name: '소나타 DN8', category: '중형', fuel: 'LPG', seats: '5인',
      price5h_weekday: '50,000', price5h_weekend: '-',
      price12h_weekday: '80,000', price12h_weekend: '80,000',
      price24h_weekday: '90,000', price24h_weekend: '90,000',
      price48h_weekday: '180,000', price48h_weekend: '180,000',
      imageId: 'sonata-dn8' },
    { id: '9', name: '그랜저 GN7', category: '대형', fuel: '하이브리드', seats: '5인',
      price5h_weekday: '80,000', price5h_weekend: '-',
      price12h_weekday: '140,000', price12h_weekend: '150,000',
      price24h_weekday: '150,000', price24h_weekend: '160,000',
      price48h_weekday: '300,000', price48h_weekend: '320,000',
      imageId: 'grandeur-gn7' },
    { id: '10', name: '제네시스 G80', category: '대형', fuel: '가솔린', seats: '5인',
      price5h_weekday: '-', price5h_weekend: '-',
      price12h_weekday: '220,000', price12h_weekend: '230,000',
      price24h_weekday: '230,000', price24h_weekend: '250,000',
      price48h_weekday: '460,000', price48h_weekend: '420,000',
      imageId: 'genesis-g80' },

    // SUV
    { id: '11', name: '투싼', category: 'SUV', fuel: '가솔린', seats: '5인',
      price5h_weekday: '60,000', price5h_weekend: '-',
      price12h_weekday: '100,000', price12h_weekend: '100,000',
      price24h_weekday: '110,000', price24h_weekend: '110,000',
      price48h_weekday: '220,000', price48h_weekend: '220,000',
      imageId: 'tucson' },
    { id: '12', name: '셀토스', category: 'SUV', fuel: '가솔린', seats: '5인',
      price5h_weekday: '40,000', price5h_weekend: '-',
      price12h_weekday: '60,000', price12h_weekend: '70,000',
      price24h_weekday: '70,000', price24h_weekend: '80,000',
      price48h_weekday: '140,000', price48h_weekend: '160,000',
      imageId: 'seltos' },
    { id: '13', name: '토레스', category: 'SUV', fuel: '가솔린', seats: '5인',
      price5h_weekday: '60,000', price5h_weekend: '-',
      price12h_weekday: '100,000', price12h_weekend: '100,000',
      price24h_weekday: '110,000', price24h_weekend: '110,000',
      price48h_weekday: '220,000', price48h_weekend: '220,000',
      imageId: 'torres' },
    { id: '14', name: '싼타페', category: 'SUV', fuel: '가솔린', seats: '7인',
      price5h_weekday: '75,000', price5h_weekend: '-',
      price12h_weekday: '120,000', price12h_weekend: '120,000',
      price24h_weekday: '130,000', price24h_weekend: '130,000',
      price48h_weekday: '260,000', price48h_weekend: '260,000',
      imageId: 'santafe' },
    { id: '15', name: '팰리세이드', category: 'SUV', fuel: '가솔린', seats: '7인',
      price5h_weekday: '-', price5h_weekend: '-',
      price12h_weekday: '140,000', price12h_weekend: '150,000',
      price24h_weekday: '150,000', price24h_weekend: '160,000',
      price48h_weekday: '300,000', price48h_weekend: '320,000',
      imageId: 'palisade' },
    { id: '16', name: '스포티지', category: 'SUV', fuel: '가솔린', seats: '5인',
      price5h_weekday: '60,000', price5h_weekend: '-',
      price12h_weekday: '100,000', price12h_weekend: '100,000',
      price24h_weekday: '110,000', price24h_weekend: '110,000',
      price48h_weekday: '220,000', price48h_weekend: '220,000',
      imageId: 'sportage' },
    { id: '17', name: '4세대 쏘렌토 FL', category: 'SUV', fuel: '가솔린', seats: '7인',
      price5h_weekday: '75,000', price5h_weekend: '-',
      price12h_weekday: '120,000', price12h_weekend: '120,000',
      price24h_weekday: '130,000', price24h_weekend: '130,000',
      price48h_weekday: '260,000', price48h_weekend: '260,000',
      imageId: 'sorento-fl' },

    // 승합 & 버스
    { id: '18', name: '카니발 리무진 4세대(7인승)', category: '승합', fuel: '디젤', seats: '7인',
      price5h_weekday: '150,000', price5h_weekend: '-',
      price12h_weekday: '190,000', price12h_weekend: '200,000',
      price24h_weekday: '380,000', price24h_weekend: '400,000',
      price48h_weekday: '760,000', price48h_weekend: '800,000',
      imageId: 'carnival-limousine-7' },
    { id: '19', name: 'All New 카니발(9인승)', category: '승합', fuel: '디젤', seats: '9인',
      price5h_weekday: '80,000', price5h_weekend: '-',
      price12h_weekday: '120,000', price12h_weekend: '130,000',
      price24h_weekday: '240,000', price24h_weekend: '260,000',
      price48h_weekday: '480,000', price48h_weekend: '520,000',
      imageId: 'carnival-9-v1' },
    { id: '20', name: 'All New 카니발 4세대(9인승)', category: '승합', fuel: '디젤', seats: '9인',
      price5h_weekday: '100,000', price5h_weekend: '-',
      price12h_weekday: '160,000', price12h_weekend: '170,000',
      price24h_weekday: '320,000', price24h_weekend: '340,000',
      price48h_weekday: '640,000', price48h_weekend: '680,000',
      imageId: 'carnival-9-v2' },
    { id: '21', name: 'All New 카니발 리무진', category: '승합', fuel: '디젤', seats: '7인',
      price5h_weekday: '100,000', price5h_weekend: '-',
      price12h_weekday: '140,000', price12h_weekend: '150,000',
      price24h_weekday: '280,000', price24h_weekend: '300,000',
      price48h_weekday: '560,000', price48h_weekend: '600,000',
      imageId: 'carnival-limousine' },
    { id: '22', name: '그랜드스타렉스', category: '승합', fuel: '디젤', seats: '12인',
      price5h_weekday: '60,000', price5h_weekend: '-',
      price12h_weekday: '120,000', price12h_weekend: '150,000',
      price24h_weekday: '240,000', price24h_weekend: '260,000',
      price48h_weekday: '480,000', price48h_weekend: '520,000',
      imageId: 'grand-starex' },
    { id: '23', name: '스타리아(9인승)', category: '승합', fuel: '디젤', seats: '9인',
      price5h_weekday: '100,000', price5h_weekend: '-',
      price12h_weekday: '170,000', price12h_weekend: '170,000',
      price24h_weekday: '340,000', price24h_weekend: '340,000',
      price48h_weekday: '680,000', price48h_weekend: '680,000',
      imageId: 'staria-9' },
    { id: '24', name: '스타리아(11인승)', category: '승합', fuel: '디젤', seats: '11인',
      price5h_weekday: '100,000', price5h_weekend: '-',
      price12h_weekday: '180,000', price12h_weekend: '180,000',
      price24h_weekday: '360,000', price24h_weekend: '360,000',
      price48h_weekday: '720,000', price48h_weekend: '720,000',
      imageId: 'staria-11' },
    { id: '25', name: '쏠라티', category: '승합', fuel: '디젤', seats: '15인',
      price5h_weekday: '250,000', price5h_weekend: '250,000',
      price12h_weekday: '300,000', price12h_weekend: '300,000',
      price24h_weekday: '600,000', price24h_weekend: '600,000',
      price48h_weekday: '1,200,000', price48h_weekend: '1,200,000',
      imageId: 'solati' },

    // 전기차
    { id: '26', name: 'Model Y', category: '전기차', fuel: '전기', seats: '5인',
      price5h_weekday: '-', price5h_weekend: '-',
      price12h_weekday: '190,000', price12h_weekend: '190,000',
      price24h_weekday: '200,000', price24h_weekend: '200,000',
      price48h_weekday: '400,000', price48h_weekend: '400,000',
      imageId: 'model-y' },
    { id: '27', name: '볼트 EV', category: '전기차', fuel: '전기', seats: '5인',
      price5h_weekday: '45,000', price5h_weekend: '-',
      price12h_weekday: '65,000', price12h_weekend: '70,000',
      price24h_weekday: '75,000', price24h_weekend: '80,000',
      price48h_weekday: '150,000', price48h_weekend: '160,000',
      imageId: 'bolt-ev' },
  ];

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodtimerent.kr/rental-guide" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{text.title}</h1>
            <p className="text-lg text-gray-600">{text.subtitle}</p>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600 mb-4"></div>
              <p className="text-gray-600">{text.loading}</p>
            </div>
          ) : (
            <>
              {/* Desktop Table - Hidden on Mobile */}
              <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">{text.image}</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">{text.name}</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">{text.type}</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">{text.fuel}</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">{text.seats}</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">5{text.hour}</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">12{text.hour}</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">24{text.hour}</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">48{text.hour}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vehicles.map((vehicle, index) => (
                    <tr key={vehicle.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="w-24 h-16 bg-white rounded flex items-center justify-center overflow-hidden">
                          {vehicle.imageId ? (
                            <img
                              src={`/car/${vehicle.imageId}.png`}
                              alt={displayVehicleName(vehicle.name)}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement!.innerHTML = `
                                  <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                  </svg>
                                `;
                              }}
                            />
                          ) : (
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900 max-w-[120px]">{displayVehicleName(vehicle.name)}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                          {displayCategory(vehicle.category)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                          {displayFuel(vehicle.fuel)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700 whitespace-nowrap">{displaySeats(vehicle.seats)}</td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs text-gray-600">
                            {text.weekday} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price5h_weekday)}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {text.weekend} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price5h_weekend)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs text-gray-600">
                            {text.weekday} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price12h_weekday)}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {text.weekend} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price12h_weekend)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs text-gray-600">
                            {text.weekday} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price24h_weekday)}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {text.weekend} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price24h_weekend)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        <div className="flex flex-col gap-1">
                          <div className="text-xs text-gray-600">
                            {text.weekday} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price48h_weekday)}</span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {text.weekend} <span className="font-semibold text-gray-900">{displayPrice(vehicle.price48h_weekend)}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
              </div>

              {/* Mobile Card View - Hidden on Desktop */}
              <div className="md:hidden space-y-3">
                {vehicles.map((vehicle) => (
                  <div key={vehicle.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Main Info - Always Visible */}
                    <div
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedRow(expandedRow === vehicle.id ? null : vehicle.id)}
                    >
                      {/* Vehicle Image */}
                      <div className="w-20 h-14 bg-white rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                        {vehicle.imageId ? (
                          <img
                            src={`/car/${vehicle.imageId}.png`}
                              alt={displayVehicleName(vehicle.name)}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.innerHTML = `
                                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                              `;
                            }}
                          />
                        ) : (
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>

                      {/* Vehicle Name & Category */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base truncate">{displayVehicleName(vehicle.name)}</h3>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            {displayCategory(vehicle.category)}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {displayFuel(vehicle.fuel)}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {displaySeats(vehicle.seats)}
                          </span>
                        </div>
                      </div>

                      {/* Toggle Icon */}
                      <div className="flex-shrink-0">
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${expandedRow === vehicle.id ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    {expandedRow === vehicle.id && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        {/* Pricing */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">{text.rateTitle}</h4>
                          <div className="space-y-2">
                            {vehicle.price5h_weekday && vehicle.price5h_weekday !== '-' && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">5{text.hour}</span>
                                <div className="text-right">
                                  <div className="text-gray-900">{text.weekday} <span className="font-semibold">{displayPrice(vehicle.price5h_weekday)}</span></div>
                                  {vehicle.price5h_weekend && vehicle.price5h_weekend !== '-' && (
                                    <div className="text-gray-600 text-xs">{text.weekend} {displayPrice(vehicle.price5h_weekend)}</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {vehicle.price12h_weekday && vehicle.price12h_weekday !== '-' && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">12{text.hour}</span>
                                <div className="text-right">
                                  <div className="text-gray-900">{text.weekday} <span className="font-semibold">{displayPrice(vehicle.price12h_weekday)}</span></div>
                                  {vehicle.price12h_weekend && vehicle.price12h_weekend !== '-' && (
                                    <div className="text-gray-600 text-xs">{text.weekend} {displayPrice(vehicle.price12h_weekend)}</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {vehicle.price24h_weekday && vehicle.price24h_weekday !== '-' && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">24{text.hour}</span>
                                <div className="text-right">
                                  <div className="text-gray-900">{text.weekday} <span className="font-semibold">{displayPrice(vehicle.price24h_weekday)}</span></div>
                                  {vehicle.price24h_weekend && vehicle.price24h_weekend !== '-' && (
                                    <div className="text-gray-600 text-xs">{text.weekend} {displayPrice(vehicle.price24h_weekend)}</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {vehicle.price48h_weekday && vehicle.price48h_weekday !== '-' && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">48{text.hour}</span>
                                <div className="text-right">
                                  <div className="text-gray-900">{text.weekday} <span className="font-semibold">{displayPrice(vehicle.price48h_weekday)}</span></div>
                                  {vehicle.price48h_weekend && vehicle.price48h_weekend !== '-' && (
                                    <div className="text-gray-600 text-xs">{text.weekend} {displayPrice(vehicle.price48h_weekend)}</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Notice */}
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{text.noticeTitle}</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {text.notices.map((notice) => (
                        <li key={notice}>• {notice}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
      </div>
    </>
  );
};

export default RentalGuidePage;
